// ==============================
// Settings → Account Management (admin only).
// List / create / edit (name, email, role) / reset password / delete staff
// accounts (AquaticsUser: COACH + ADMIN). Backed by /auth/accounts.
// ==============================

import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  EditOutlined,
  HowToReg,
  LockReset,
  PersonAddAlt1,
  PersonOff,
  Search,
} from '@mui/icons-material';

import MiniDrawer from '../../components/MiniDrawer';
import AppBarSpacer from '../../components/common/AppBarSpacer';
import PageHeader from '../../components/common/PageHeader';
import {
  ManagementTableActionCell,
  ManagementTableHeaderCell,
} from '../../components/common/ManagementTable';
import { EmptyValue } from '../../components/common/EmptyValue';
import { rowHoverBg } from '../../utils/rowHighlight';
import { notifyActionSuccess, notifyAPIClient } from '../../utils/toast';
import { useTranslation } from '../../i18n';
import {
  AccountRole,
  AquaticsAccount,
  createAccount,
  deactivateAccount,
  reactivateAccount,
  listAccounts,
  resetAccountPassword,
  updateAccount,
} from '../../utils/accountApi';

const apiErrorMessage = (error: unknown, fallback: string): string => {
  const message = (
    error as { response?: { data?: { message?: string } } }
  )?.response?.data?.message;
  return message || fallback;
};

const AccountManagement = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);

  const currentUser = JSON.parse(
    localStorage.getItem('aquatics_hub_2025_user') || 'null',
  ) as { id?: number } | null;
  const currentUserId = currentUser?.id;

  const [accounts, setAccounts] = useState<AquaticsAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<AccountRole | ''>('');

  // Create / edit dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AquaticsAccount | null>(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'COACH' as AccountRole,
  });
  const [saving, setSaving] = useState(false);

  // Reset password dialog
  const [resetTarget, setResetTarget] = useState<AquaticsAccount | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  // Deactivate confirm dialog
  const [deactivateTarget, setDeactivateTarget] =
    useState<AquaticsAccount | null>(null);
  const [processing, setProcessing] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAccounts({
        page: page + 1,
        limit: rowsPerPage,
        search,
        role: roleFilter,
      });
      setAccounts(data.accounts);
      setTotalItems(data.pagination.total);
    } catch (error) {
      notifyAPIClient(apiErrorMessage(error, t('account.loadFailed')));
    } finally {
      setLoading(false);
    }
    // NB: `search` is intentionally omitted — it is applied on Enter (see the
    // search field), not on every keystroke.
  }, [page, rowsPerPage, roleFilter, search, t]);

  // Auto-fetch on paging / role change only — search is applied on Enter so we
  // deliberately exclude `fetchAccounts` (which changes with `search`) here.
  useEffect(() => {
    void fetchAccounts();
  }, [page, rowsPerPage, roleFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', password: '', role: 'COACH' });
    setFormOpen(true);
  };

  const openEdit = (account: AquaticsAccount) => {
    setEditing(account);
    setForm({
      name: account.name || '',
      email: account.email,
      password: '',
      role: account.role,
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await updateAccount(editing.id, {
          name: form.name,
          email: form.email,
          role: form.role,
        });
        notifyActionSuccess(t('account.updated'));
      } else {
        await createAccount({
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
        });
        notifyActionSuccess(t('account.created'));
      }
      setFormOpen(false);
      void fetchAccounts();
    } catch (error) {
      notifyAPIClient(apiErrorMessage(error, t('account.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      await resetAccountPassword(resetTarget.id, newPassword);
      notifyActionSuccess(t('account.passwordReset'));
      setResetTarget(null);
      setNewPassword('');
    } catch (error) {
      notifyAPIClient(apiErrorMessage(error, t('account.passwordResetFailed')));
    } finally {
      setResetting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setProcessing(true);
    try {
      await deactivateAccount(deactivateTarget.id);
      notifyActionSuccess(t('account.deactivatedSuccess'));
      setDeactivateTarget(null);
      void fetchAccounts();
    } catch (error) {
      notifyAPIClient(apiErrorMessage(error, t('account.deactivatedFailed')));
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivate = async (account: AquaticsAccount) => {
    setProcessing(true);
    try {
      await reactivateAccount(account.id);
      notifyActionSuccess(t('account.reactivatedSuccess'));
      void fetchAccounts();
    } catch (error) {
      notifyAPIClient(apiErrorMessage(error, t('account.reactivatedFailed')));
    } finally {
      setProcessing(false);
    }
  };

  const createValid =
    form.name.trim() !== '' &&
    /^\S+@\S+\.\S+$/.test(form.email) &&
    (editing || form.password.length >= 8);

  return (
    <div className="flex h-screen">
      <MiniDrawer open={open} setOpen={setOpen} />
      <main className="flex-1 overflow-auto app-page-bg">
        <AppBarSpacer />
        <div className="h-full px-6 py-4">
          <PageHeader
            title={t('account.pageTitle')}
            subtitle={t('account.pageSubtitle')}
          />

          {/* Add account — outside the table, under the page divider (like other screens) */}
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddAlt1 />}
              onClick={openCreate}
              sx={{ color: 'white' }}
            >
              {t('account.addAccount')}
            </Button>
          </Box>

          <Paper>
            <Box
              p={2}
              display="flex"
              gap={2}
              flexWrap="wrap"
              alignItems="center"
            >
              <TextField
                size="small"
                placeholder={t('account.searchPlaceholder')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    setPage(0);
                    void fetchAccounts();
                  }
                }}
                sx={{ width: 360, maxWidth: '100%' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="account-role-filter">
                  {t('account.role')}
                </InputLabel>
                <Select
                  labelId="account-role-filter"
                  label={t('account.role')}
                  value={roleFilter}
                  onChange={(event) => {
                    setRoleFilter(event.target.value as AccountRole | '');
                    setPage(0);
                  }}
                >
                  <MenuItem value="">{t('account.allRoles')}</MenuItem>
                  <MenuItem value="ADMIN">{t('account.roleAdmin')}</MenuItem>
                  <MenuItem value="COACH">{t('account.roleCoach')}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TableContainer>
              <Table size="small" padding="checkbox">
                <TableHead>
                  <TableRow>
                    <ManagementTableHeaderCell>
                      {t('account.colId')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('account.colName')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('account.colEmail')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('account.colRole')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('account.colCreatedAt')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell sticky>
                      {t('account.colActions')}
                    </ManagementTableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : accounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                        {t('account.noAccountsFound')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    accounts.map((account) => {
                      const isSelf = account.id === currentUserId;
                      const isDeactivated = account.deletionMark;
                      return (
                        <TableRow
                          key={account.id}
                          sx={{
                            '&:hover': { backgroundColor: rowHoverBg },
                            // Gray out deactivated accounts.
                            ...(isDeactivated && {
                              opacity: 0.5,
                              '& .MuiTableCell-root': {
                                color: 'text.disabled',
                              },
                            }),
                          }}
                        >
                          <TableCell>{account.id}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {account.name || <EmptyValue />}
                            {isSelf ? t('account.you') : ''}
                            {isDeactivated && (
                              <Chip
                                size="small"
                                label={t('account.deactivated')}
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {account.email}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={t(
                                account.role === 'ADMIN'
                                  ? 'account.roleAdmin'
                                  : 'account.roleCoach',
                              )}
                              color={
                                account.role === 'ADMIN' ? 'primary' : 'default'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {new Date(account.createdAt).toLocaleDateString()}
                          </TableCell>
                          <ManagementTableActionCell>
                            {isDeactivated ? (
                              <Tooltip title={t('account.tooltipReactivate')}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    aria-label={t('account.ariaReactivate')}
                                    disabled={processing}
                                    onClick={() =>
                                      void handleReactivate(account)
                                    }
                                  >
                                    <HowToReg fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <>
                                <Tooltip title={t('account.tooltipEdit')}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label={t('account.ariaEdit')}
                                    onClick={() => openEdit(account)}
                                  >
                                    <EditOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('account.tooltipResetPassword')}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label={t('account.ariaResetPassword')}
                                    onClick={() => {
                                      setResetTarget(account);
                                      setNewPassword('');
                                    }}
                                  >
                                    <LockReset fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    isSelf
                                      ? t('account.cannotDeactivateSelf')
                                      : t('account.tooltipDeactivate')
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      aria-label={t('account.ariaDeactivate')}
                                      disabled={isSelf}
                                      onClick={() =>
                                        setDeactivateTarget(account)
                                      }
                                    >
                                      <PersonOff fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </ManagementTableActionCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(_event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              labelRowsPerPage={t('account.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                t('account.displayedRows', {
                  from,
                  to,
                  count: count === -1 ? `>${to}` : count,
                })
              }
              getItemAriaLabel={(type) => t(`account.pagination.${type}`)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Paper>
        </div>
      </main>

      {/* Create / edit dialog */}
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {editing ? t('account.editTitle') : t('account.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('account.fieldName')}
              value={form.name}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, name: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label={t('account.fieldEmail')}
              type="email"
              value={form.email}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, email: event.target.value }))
              }
              fullWidth
            />
            {!editing && (
              <TextField
                label={t('account.fieldPassword')}
                type="password"
                value={form.password}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, password: event.target.value }))
                }
                helperText={t('account.passwordHint')}
                fullWidth
              />
            )}
            <FormControl fullWidth>
              <InputLabel id="account-form-role">
                {t('account.role')}
              </InputLabel>
              <Select
                labelId="account-form-role"
                label={t('account.role')}
                value={form.role}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    role: event.target.value as AccountRole,
                  }))
                }
              >
                <MenuItem value="ADMIN">{t('account.roleAdmin')}</MenuItem>
                <MenuItem value="COACH">{t('account.roleCoach')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            {t('account.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving || !createValid}
          >
            {editing ? t('account.save') : t('account.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog
        open={resetTarget !== null}
        onClose={() => setResetTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('account.resetTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('account.resetBody', {
              name: resetTarget?.name || resetTarget?.email || '',
            })}
          </DialogContentText>
          <TextField
            autoFocus
            label={t('account.newPassword')}
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            helperText={t('account.passwordHint')}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)} disabled={resetting}>
            {t('account.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleResetPassword()}
            disabled={resetting || newPassword.length < 8}
          >
            {t('account.reset')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirm dialog */}
      <Dialog
        open={deactivateTarget !== null}
        onClose={() => setDeactivateTarget(null)}
      >
        <DialogTitle>{t('account.deactivateTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('account.deactivateBody', {
              name: deactivateTarget?.name || deactivateTarget?.email || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeactivateTarget(null)}
            disabled={processing}
          >
            {t('account.cancel')}
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => void handleDeactivate()}
            disabled={processing}
          >
            {t('account.deactivate')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AccountManagement;
